import { useMemo } from 'react';
import type { PageBreadCrumb } from '@firsttable/types';
import { countFormat, roundToClosest } from '@discover-lib/dataFormats';
import useRegularOnly from '@discover-data/stores/hooks/regularOnly/useRegularOnly';
import { DialogTrigger } from '@discover-ui/dialog';
import Button from '@discover-ui/button';
import CityHeroComponent from '@/components/organisms/CityHero';
import HowItWorksModal from '@/components/organisms/HowItWorksModal';

export type CityHeroProps = {
  breadcrumbs: { edges: PageBreadCrumb[] };
  regionTitle: string;
  headerTitle: string;
  templateSessionName: string;
  templateSession: string;
  slug: string;
  restaurantCount: number;
};

const CityHero = ({
  breadcrumbs: cityBreadcrumbs,
  regionTitle,
  headerTitle,
  templateSessionName,
  templateSession,
  slug,
  restaurantCount,
}: CityHeroProps) => {
  const [regularOnly] = useRegularOnly();

  const breadcrumbs: PageBreadCrumb[] = useMemo(() => {
    const breadcrumbsItems = cityBreadcrumbs.edges.map((item) => ({ ...item, selected: false }));
    // Add breakfast, lunch or Last Table breadcrumb
    if (templateSessionName && templateSession) {
      breadcrumbsItems.push({
        selected: false,
        node: {
          id: '',
          slug: `${slug}/${templateSession.replace('dinner2', 'last-table')}`,
          menuTitle: `${templateSessionName} Deals`,
        },
      });
    }
    return breadcrumbsItems;
  }, [cityBreadcrumbs.edges, templateSessionName, templateSession, slug]);

  // Conditional text for Australia and UK
  const ausUk = process.env.NEXT_PUBLIC_SITE_ID === '2' || process.env.NEXT_PUBLIC_SITE_ID === '8';
  const headerSubtitle = ausUk
    ? 'Get 50% off the food bill at top restaurants'
    : 'Dine early, save money';

  const infoTitle = regularOnly ? 'Book any table' : 'Your table, your time';

  const roundTo = 10 ** (`${restaurantCount}`.length - 1);
  const roundedCount = roundToClosest(restaurantCount, roundTo);
  const infoDescription = regularOnly
    ? `Find tables at more than ${countFormat(roundedCount)} restaurants in ${regionTitle} in one place. Start exploring now!`
    : 'First Table is your go-to for 50% off restaurant reservations. Start exploring below!';

  const howItWorksButton = (
    <HowItWorksModal
      trackingSource="list_hero_button"
      renderTrigger={
        <DialogTrigger asChild>
          <Button kind="normal" scale="md">
            Learn more
          </Button>
        </DialogTrigger>
      }
    />
  );

  return (
    <CityHeroComponent
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      infoTitle={infoTitle}
      infoDescription={infoDescription}
      breadcrumbs={breadcrumbs}
      showFaqLink={!regularOnly}
      subtextCta={howItWorksButton}
    />
  );
};

export default CityHero;
